import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import NumberFormat, { NumberFormatProps } from 'react-number-format'

import { CurrencyCode } from '@cash-web/protos/squareup/common/currency_codes.pb'
import { convertDecimalMoneyToCents } from '@cash-web/shared-util-formatting'

import { formatMoney } from '../utils/formatMoney'

export type GenericMoneyInputProps = {
  ['data-testid']?: string
  ariaLabel?: string
  currencyCode?: CurrencyCode
  currencyPrefix?: string
  forceDecimalPoint?: boolean
  formattedValue: string
  fractionalValuesOnly?: boolean
  isRequired?: boolean
  maximumAmount?: number
  minimumAmount?: number
  name: string
  placeholder?: string
  setFormattedValue: (value: string) => void
  shouldAutoFocus?: boolean
  validationErrorMessages?: {
    overMaximumAmount?: string
  }
  decimalScale?: 0 | 2
} & Omit<NumberFormatProps, 'decimalScale'>

export const GenericMoneyInput = ({
  ariaLabel,
  currencyCode = CurrencyCode.USD,
  customInput,
  forceDecimalPoint,
  formattedValue,
  fractionalValuesOnly,
  isRequired = true,
  maximumAmount,
  minimumAmount,
  name,
  placeholder,
  setFormattedValue,
  shouldAutoFocus,
  validationErrorMessages,
  decimalScale,
  ...props
}: GenericMoneyInputProps) => {
  const { t } = useTranslation()
  const { setValue, register, formState } = useFormContext()

  const { errors, dirtyFields, isSubmitted } = formState

  const defaultPlaceholder = fractionalValuesOnly ? `$0.00` : undefined

  const { onBlur, ...inputProps } = register(name, {
    required: {
      value: isRequired,
      message: t('Amount is required.', { context: 'Error message: amount not specified' }),
    },
    validate: (value: string) => {
      const valueAsNumber = Number(value)
      if (value != null && Number.isNaN(valueAsNumber)) {
        return t('Amount must be a number.', {
          context: 'Error message: amount is not a number',
        })
      }
      if (minimumAmount != null && valueAsNumber < minimumAmount) {
        return t('Amount must be at least {amount}.', {
          amount: formatMoney({ amount: minimumAmount, currency_code: currencyCode }),
          context: 'Error message: amount is less than minimum allowed amount',
        })
      }
      if (maximumAmount != null && valueAsNumber > maximumAmount) {
        return (
          validationErrorMessages?.overMaximumAmount ??
          t('Amount must be less than or equal to {amount}.', {
            amount: formatMoney({ amount: maximumAmount, currency_code: currencyCode }),
            context: 'Error message: amount is more than maximum allowed amount',
          })
        )
      }
      return true
    },
  })

  const error = errors[name]
  const showError = error && (error?.type !== 'required' || isSubmitted)

  return (
    <NumberFormat
      prefix={fractionalValuesOnly ? `${props.currencyPrefix}0.` : props.currencyPrefix}
      {...props}
      {...inputProps}
      allowEmptyFormatting={placeholder || fractionalValuesOnly ? false : true}
      allowNegative={false}
      aria-label={ariaLabel}
      autoComplete="off"
      autoFocus={shouldAutoFocus}
      className={showError ? 'error' : ''}
      customInput={customInput}
      data-testid={props['data-testid']}
      decimalScale={decimalScale ?? 2}
      // enforce two decimal places once user has shown intention to add cents
      fixedDecimalScale={formattedValue.includes('.')}
      // only need to handle for fractionalValuesOnly case; prefix is counted in the length
      // should only ever be length 5 in format $0.##S
      maxLength={fractionalValuesOnly ? 5 : undefined}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        if (dirtyFields[name]) {
          onBlur(e)
        }
        // only need to handle for fractionalValuesOnly case
        // fixedDecimalScale handles padding with zeros for other cases
        let val = e.target.value
        while (fractionalValuesOnly && val.length < 5 && val.length > 1) {
          val += '0'
        }
        setFormattedValue(val)
      }}
      onValueChange={target => {
        let value = target.value
        if (!fractionalValuesOnly) {
          value = target.value === '' ? '' : convertDecimalMoneyToCents({ decimalAmount: target.value })
        }
        setFormattedValue(target.formattedValue)
        setValue(name, value)
      }}
      placeholder={placeholder || defaultPlaceholder}
      thousandSeparator={true}
      value={formattedValue}
    />
  )
}
